import { config } from 'dotenv';
import { z } from 'zod';
import {
  EnvSchema,
  LoadEnvOptions,
  ValidationError,
  ValidationResult,
  TypeGeneratorOptions,
} from './types';

// Define a type for Node.js errors
interface NodeError extends Error {
  code?: string;
}

function createZodSchema(schema: EnvSchema): z.ZodObject<any> {
  const zodSchema: Record<string, z.ZodType> = {};

  for (const [key, value] of Object.entries(schema)) {
    if (value instanceof z.ZodType) {
      zodSchema[key] = value;
    } else if (Array.isArray(value)) {
      zodSchema[key] = z.enum(value as [string, ...string[]]);
    } else {
      switch (value) {
        case 'string':
          zodSchema[key] = z.string();
          break;
        case 'number':
          zodSchema[key] = z.string()
            .refine((val) => !isNaN(Number(val)), {
              message: 'Invalid number format',
            })
            .transform((val) => Number(val));
          break;
        case 'boolean':
          zodSchema[key] = z.enum(['true', 'false']).transform((val) => val === 'true');
          break;
        case 'array':
          zodSchema[key] = z.string().refine((val) => {
            try {
              const parsed = JSON.parse(val);
              return Array.isArray(parsed);
            } catch {
              // fallback: allow comma-separated string as array
              return val.includes(',');
            }
          }, { message: 'Must be a JSON array or comma-separated values' })
            .transform((val) => {
              try {
                const parsed = JSON.parse(val);
                if (Array.isArray(parsed)) return parsed;
              } catch { }
              return val.split(',');
            });
          break;
        case 'object':
          zodSchema[key] = z.string().transform((val) => {
            const parsed = JSON.parse(val);
            if (typeof parsed !== 'object' || Array.isArray(parsed) || parsed === null) {
              throw new Error('Must be a JSON object');
            }
            return parsed;
          });
          break;
        default:
          zodSchema[key] = z.string();
      }
    }
  }

  return z.object(zodSchema);
}

export function loadEnv(options: LoadEnvOptions): ValidationResult {
  const { schema, path, encoding, override, debug } = options;

  // Load environment variables from .env file if path is provided
  if (path) {
    try {
      const result = config({ path, encoding, override, debug });
      if (result.error) {
        throw result.error;
      }
    } catch (error: unknown) {
      const nodeError = error as NodeError;
      if (nodeError.code !== 'ENOENT') {
        throw error;
      }
      // Ignore missing .env file
    }
  }

  const zodSchema = createZodSchema(schema);
  const validationResult = zodSchema.safeParse(process.env);

  if (!validationResult.success) {
    const errors: ValidationError[] = validationResult.error.errors.map((err) => ({
      key: err.path.join('.'),
      message: err.message,
      value: process.env[err.path[0] as string],
    }));

    return {
      success: false,
      errors,
      env: {},
    };
  }

  return {
    success: true,
    env: validationResult.data,
  };
}

export function generateTypes(options: TypeGeneratorOptions): string {
  const { schema, outputPath } = options;
  let typeDefinitions = 'declare global {\n  namespace NodeJS {\n    interface ProcessEnv {\n';

  for (const [key, value] of Object.entries(schema)) {
    let type: string;

    if (value instanceof z.ZodType) {
      type = 'string'; // Default to string for custom Zod types
    } else if (Array.isArray(value)) {
      type = value.map((v) => `'${v}'`).join(' | ');
    } else {
      switch (value) {
        case 'number':
          type = 'number';
          break;
        case 'boolean':
          type = 'boolean';
          break;
        case 'array':
          type = 'string[]';
          break;
        case 'object':
          type = 'Record<string, unknown>';
          break;
        default:
          type = 'string';
      }
    }

    typeDefinitions += `      ${key}: ${type};\n`;
  }

  typeDefinitions += '    }\n  }\n}\n\nexport {};';

  if (outputPath) {
    // TODO: Implement file writing
  }

  return typeDefinitions;
}

export function validateEnv(schema: EnvSchema): ValidationResult {
  return loadEnv({ schema });
}

export function parseEnvFile(path: string): Record<string, string> {
  const result = config({ path });
  if (result.error) {
    throw result.error;
  }
  return result.parsed || {};
}